// useTestSession.js
import { useState, useEffect, useCallback, useRef } from "react";
import {
  createSession,
  getSession,
  hasSession,
  getQuestionIds,
  getCurrentIndex,
  setCurrentIndex,
  getLoadedQuestion,
  setLoadedQuestion,
  isQuestionLoaded,
  getQuestionCount,
  getPhase,
  setPhase,
  getRoll,
  setRoll,
  setStudentInfo,
  getStudentInfo,
  clearSession,
} from "../Cache/testSessionCache";
import {
  getTestQuestions,
  getQuestionResources,
  buildQuestionFromRows,
} from "../../../Backend/fetchTest";
import { createResponse, submitResponse } from "../../../Backend/handleResponse";


export default function useTestSession(testId) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phase, setPhaseState] = useState("checkin");
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const prefetchingRef = useRef(false);
  const responseDocIdRef = useRef(null);

  // ── Phase helper ──
  const updatePhase = useCallback((newPhase) => {
    setPhaseState(newPhase);
    setPhase(testId, newPhase);
  }, [testId]);

  // ── Load a question by index ──
  const loadQuestion = useCallback(async (index) => {
    const questionIds = getQuestionIds(testId);
    if (index < 0 || index >= questionIds.length) return null;

    const qId = questionIds[index];

    if (isQuestionLoaded(testId, qId)) {
      return getLoadedQuestion(testId, qId);
    }

    const result = await getQuestionResources(qId);
    if (!result.success) throw new Error(result.msg);

    const question = buildQuestionFromRows(result.rows, index + 1);
    if (!question) throw new Error(`No resources found for question ${index + 1}`);

    setLoadedQuestion(testId, qId, question);
    return question;
  }, [testId]);

  // ── Prefetch next question ──
  const prefetchNext = useCallback(async (currentIdx) => {
    if (prefetchingRef.current) return;
    const questionIds = getQuestionIds(testId);
    const nextIdx = currentIdx + 1;

    if (nextIdx >= questionIds.length) return;
    if (isQuestionLoaded(testId, questionIds[nextIdx])) return;

    prefetchingRef.current = true;
    try {
      await loadQuestion(nextIdx);
    } catch {
      // Silent fail
    }
    prefetchingRef.current = false;
  }, [testId, loadQuestion]);

  // ── Initialize ──
  useEffect(() => {
    async function init() {
      setLoading(true);
      setError(null);

      try {
        if (hasSession(testId)) {
          const session = getSession(testId);
          const idx = session.currentIndex;
          setQuestionIndex(idx);
          setTotalQuestions(session.questionIds.length);

          const cachedPhase = session.phase || "checkin";

          // If checkin phase but student already registered, skip to briefing
          if (cachedPhase === "checkin" && getRoll(testId)) {
            setPhaseState("briefing");
            setPhase(testId, "briefing");
          } else if (cachedPhase === "question") {
            const qId = session.questionIds[idx];
            if (isQuestionLoaded(testId, qId)) {
              setCurrentQuestion(getLoadedQuestion(testId, qId));
              setPhaseState("question");
            } else {
              setPhaseState("briefing");
              setPhase(testId, "briefing");
            }
          } else {
            setPhaseState(cachedPhase);
          }

          setLoading(false);
          return;
        }

        const result = await getTestQuestions(testId);
        if (!result.success) {
          setError(result.msg);
          setLoading(false);
          return;
        }

        if (!result.questionIds || result.questionIds.length === 0) {
          setError("This test has no questions.");
          setLoading(false);
          return;
        }

        createSession(testId, result.questionIds);
        setTotalQuestions(result.questionIds.length);
        setQuestionIndex(0);
        setPhaseState("checkin");
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    }

    init();
  }, [testId]);

  // ── Actions ──

  const handleCheckInComplete = useCallback((studentFields) => {
    // studentFields = { name, className, roll }
    setRoll(testId, studentFields.roll);
    setStudentInfo(testId, {
      name: studentFields.name,
      className: studentFields.className,
    });
    updatePhase("briefing");
    prefetchNext(-1);
  }, [testId, updatePhase, prefetchNext]);

  const handleBriefingStart = useCallback(async () => {
    setLoading(true);
    try {
      const question = await loadQuestion(questionIndex);
      const roll = getRoll(testId);
      const studentInfo = getStudentInfo(testId);
      const questionIds = getQuestionIds(testId);
      const qId = questionIds[questionIndex];
      console.log("res", roll, studentInfo, qId);

      const responseResult = await createResponse(roll, studentInfo, qId);

      if (!responseResult.success) {
        setError(`Failed to create response: ${responseResult.msg}`);
        setLoading(false);
        return;
      }
      console.log("res", responseResult.id);
      responseDocIdRef.current = responseResult.id;

      setCurrentQuestion(question);
      updatePhase("question");
      setLoading(false);
      prefetchNext(questionIndex);
    } catch (err) {
      setError(`Failed to load question ${questionIndex + 1}: ${err.message}`);
      setLoading(false);
    }
  }, [questionIndex, testId, loadQuestion, updatePhase, prefetchNext]);

  const goToNext = useCallback(() => {
    const nextIdx = questionIndex + 1;
    const total = getQuestionCount(testId);

    if (nextIdx >= total) {
      updatePhase("finished");
      clearSession(testId);
      return;
    }

    setQuestionIndex(nextIdx);
    setCurrentIndex(testId, nextIdx);
    updatePhase("briefing");
    prefetchNext(nextIdx);
  }, [questionIndex, testId, updatePhase, prefetchNext]);


  const handleSubmit = useCallback(async (answer, violations = []) => {  // ← new param
    setLoading(true);
    try {
      const responseId = responseDocIdRef.current;
      const submissionResult = await submitResponse(responseId, answer, violations); // ← pass it
      if (!submissionResult.success) {
        setError(`Failed to submit response: ${submissionResult.msg}`);
        setLoading(false);
        return;
      }
      setLoading(false);
      goToNext();
    } catch (err) {
      setError(`Failed to submit response question ${questionIndex + 1}: ${err.message}`);
      setLoading(false);
    }
  }, [questionIndex, goToNext, currentQuestion]);


  return {
    loading,
    error,
    phase,
    currentQuestion,
    questionIndex,
    totalQuestions,
    handleCheckInComplete,
    handleBriefingStart,
    handleSubmit
  };
}